import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { ClientDto } from '../../api/ClientApi';
import { Box, IconButton, Paper, TablePagination } from '@mui/material';
import { StyledTableCell } from '../../common/components/styled/StyledTable';
import { useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { MobileCardRow, mobileHiddenHeadSx, useIsMobile } from '../../common/components/mobile/MobilePrimitives';

function createData(id?: number, name?: string, shortName?: string, phone?: string, address?: string) {
    return { id, name, shortName, phone, address };
}

type ClientsTableProps = {
    clients: ClientDto[] | null;
};

export default function ClientsTable(props: ClientsTableProps) {
    const { clients } = props;

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const navigate = useNavigate();

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const goToClientDetails = (clientId?: number) => {
        if (!!clientId) {
            navigate(clientId.toString());
        }
    };

    const rows = clients?.map((client) => {
        return createData(client.id, client.name, client.shortName, client.phone, client.address);
    });

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (rows?.length ?? 0)) : 0;

    const visibleRows = React.useMemo(
        () => (!rows ? [] : [...rows].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
        [page, rowsPerPage, clients],
    );

    React.useEffect(() => {
        if (!rows || rows?.length <= page * rowsPerPage) {
            setPage(0);
        }
    }, [rows, page]);

    const isMobile = useIsMobile();

    return (
        <Box sx={{ width: '100%' }}>
            <Paper sx={{ width: '100%', mb: 2 }}>
                <TableContainer
                    sx={{
                        minHeight: '100px',
                        margin: 'auto',
                    }}
                >
                    <Table size="small" stickyHeader aria-label="simple table">
                        <TableHead sx={mobileHiddenHeadSx}>
                            <TableRow>
                                <StyledTableCell>Id</StyledTableCell>
                                <StyledTableCell align="center">Ime</StyledTableCell>
                                <StyledTableCell align="center">Skr. ime</StyledTableCell>
                                <StyledTableCell align="center">Telefon</StyledTableCell>
                                <StyledTableCell align="center">Adresa</StyledTableCell>
                                <StyledTableCell align="center" sx={{ width: 100 }}>
                                    Akcija
                                </StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows?.map((row) => {
                                if (!!row.id) {
                                    if (isMobile) {
                                        return (
                                            <MobileCardRow
                                                key={row.id}
                                                title={row.name ?? '/'}
                                                subtitle={row.shortName}
                                                actions={
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => goToClientDetails(row.id)}
                                                    >
                                                        <InfoOutlinedIcon />
                                                    </IconButton>
                                                }
                                                fields={[
                                                    {
                                                        label: 'Telefon',
                                                        value: row.phone ? (
                                                            <a href={'tel:' + row.phone}>{row.phone}</a>
                                                        ) : (
                                                            '/'
                                                        ),
                                                    },
                                                    { label: 'Adresa', value: row.address ?? '/' },
                                                ]}
                                                onClick={() => goToClientDetails(row.id)}
                                            />
                                        );
                                    }
                                    return (
                                        <TableRow key={row.id}>
                                            <TableCell component="th" scope="row">
                                                {row.id ?? '/'}
                                            </TableCell>
                                            <TableCell align="center">{row.name ?? '/'}</TableCell>
                                            <TableCell align="center">{row.shortName ?? '/'}</TableCell>
                                            <TableCell align="center">{row.phone ?? '/'}</TableCell>
                                            <TableCell align="center">{row.address ?? '/'}</TableCell>
                                            <TableCell align="center" sx={{ width: 100, py: 0 }}>
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => goToClientDetails(row.id)}
                                                    sx={{ m: 0 }}
                                                >
                                                    <InfoOutlinedIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                            })}
                            {emptyRows > 0 && (
                                <TableRow
                                    style={{
                                        height: 40 * emptyRows,
                                    }}
                                >
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={rows?.length ?? 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
}
